import { 
  Connection, 
  ConnectionConfig,
  Commitment,
  SendOptions,
  TransactionSignature,
  BlockhashWithExpiryBlockHeight,
  TransactionConfirmationStrategy,
  RpcResponseAndContext,
  SignatureResult
} from "@solana/web3.js";

// RPC endpoints con failover automatico
// IMPORTANT: non mettere API key private hardcodate nel frontend.
const DEFAULT_RPC_ENDPOINTS = [
  "https://rpc.ankr.com/solana",
  "https://api.mainnet-beta.solana.com",
  "https://solana-mainnet.g.alchemy.com/v2/demo",
];

function getRpcEndpoints(): string[] {
  const envRpc = (import.meta as any)?.env?.VITE_SOLANA_RPC as string | undefined;
  const endpoints = [envRpc, ...DEFAULT_RPC_ENDPOINTS].filter(
    (v): v is string => typeof v === "string" && v.length > 0,
  );

  // de-dup
  return Array.from(new Set(endpoints));
}

const MAX_RETRIES = 4;
const TIMEOUT_MS = 15000; // 15s per mobile

/**
 * ResilientConnection
 *
 * Nota: non possiamo “mutare” in modo sicuro una Connection interna (campi privati).
 * Quindi per ogni tentativo creiamo una Connection temporanea sull’endpoint corrente.
 */
export class ResilientConnection extends Connection {
  private endpoints: string[];
  private currentEndpointIndex: number = 0;
  private failedEndpoints: Set<number> = new Set();
  private baseConfig: ConnectionConfig;

  constructor(endpoints: string[] = getRpcEndpoints(), config?: ConnectionConfig) {
    const cfg = config || { commitment: "confirmed" };
    super(endpoints[0], cfg);
    this.endpoints = endpoints;
    this.baseConfig = cfg;
  }

  private getCurrentEndpoint(): string {
    return this.endpoints[this.currentEndpointIndex] || this.endpoints[0];
  }

  private createAttemptConnection(endpoint: string): Connection {
    // ConnectionConfig può includere fetch/httpHeaders ecc. Se non ci sono, ok.
    return new Connection(endpoint, this.baseConfig);
  }

  private switchToNextEndpoint(): boolean {
    this.failedEndpoints.add(this.currentEndpointIndex);

    for (let i = 0; i < this.endpoints.length; i++) {
      if (!this.failedEndpoints.has(i)) {
        this.currentEndpointIndex = i;
        console.log(`🔄 Switching to RPC: ${this.endpoints[i].substring(0, 50)}...`);
        return true;
      }
    }

    // Reset se tutti falliti
    this.failedEndpoints.clear();
    this.currentEndpointIndex = 0;
    return false;
  }

  private isRetriableError(error: any): boolean {
    if (!error) return false;

    const errorString = error.toString().toLowerCase();
    const message = error.message?.toLowerCase() || "";
    const errorCode = error.code || error.error?.code;

    return (
      errorCode === 401 ||
      errorCode === 403 ||
      errorCode === 429 ||
      errorCode === 502 ||
      errorCode === 503 ||
      errorCode === -32401 || // unauthorized
      errorCode === -32429 || // Rate limit
      errorString.includes("401") ||
      errorString.includes("403") ||
      errorString.includes("unauthorized") ||
      errorString.includes("forbidden") ||
      errorString.includes("timeout") ||
      errorString.includes("rate limit") ||
      errorString.includes("too many requests") ||
      message.includes("401") ||
      message.includes("403") ||
      message.includes("unauthorized") ||
      message.includes("forbidden") ||
      message.includes("failed to fetch") ||
      message.includes("load failed") ||
      message.includes("network error") ||
      message.includes("econnrefused")
    );
  }

  private async executeWithFallback<T>(
    operation: (conn: Connection) => Promise<T>,
    operationName: string,
  ): Promise<T> {
    let lastError: any;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      const endpoint = this.getCurrentEndpoint();
      const conn = this.createAttemptConnection(endpoint);

      try {
        const result = await Promise.race([
          operation(conn),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("RPC timeout")), TIMEOUT_MS),
          ),
        ]);

        // Success - reset failed endpoints
        if (this.failedEndpoints.size > 0) {
          this.failedEndpoints.clear();
        }

        return result;
      } catch (error: any) {
        lastError = error;
        console.warn(
          `⚠️ ${operationName} attempt ${attempt + 1} failed (${endpoint}):`,
          error?.message || error,
        );

        if (this.isRetriableError(error)) {
          if (this.switchToNextEndpoint()) {
            continue;
          }
        }

        if (attempt < MAX_RETRIES - 1) {
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
        }
      }
    }

    throw lastError;
  }

  // Override metodi critici con fallback

  async getLatestBlockhash(
    commitment?: Commitment,
  ): Promise<BlockhashWithExpiryBlockHeight> {
    return this.executeWithFallback(
      (c) => c.getLatestBlockhash(commitment || "confirmed"),
      "getLatestBlockhash",
    );
  }

  async sendRawTransaction(
    rawTransaction: Buffer | Uint8Array,
    options?: SendOptions,
  ): Promise<TransactionSignature> {
    return this.executeWithFallback(
      (c) =>
        c.sendRawTransaction(rawTransaction, {
          skipPreflight: false,
          preflightCommitment: "confirmed",
          maxRetries: 3,
          ...options,
        }),
      "sendRawTransaction",
    );
  }

  async confirmTransaction(
    strategy: TransactionConfirmationStrategy | string,
    commitment?: Commitment,
  ): Promise<RpcResponseAndContext<SignatureResult>> {
    return this.executeWithFallback(
      (c) => c.confirmTransaction(strategy as any, commitment || "confirmed"),
      "confirmTransaction",
    );
  }

  async getBalance(publicKey: any, commitment?: Commitment): Promise<number> {
    return this.executeWithFallback(
      (c) => c.getBalance(publicKey, commitment),
      "getBalance",
    );
  }

  async getTransaction(signature: string, options?: any): Promise<any> {
    return this.executeWithFallback(
      (c) => c.getTransaction(signature, options),
      "getTransaction",
    );
  }

  async getAccountInfo(publicKey: any, commitment?: Commitment): Promise<any> {
    return this.executeWithFallback(
      (c) => c.getAccountInfo(publicKey, commitment),
      "getAccountInfo",
    );
  }

  async getMultipleAccountsInfo(
    publicKeys: any[],
    commitment?: Commitment,
  ): Promise<any[]> {
    return this.executeWithFallback(
      (c) => c.getMultipleAccountsInfo(publicKeys, commitment),
      "getMultipleAccountsInfo",
    );
  }

  async getSignatureStatus(signature: string, config?: any): Promise<any> {
    return this.executeWithFallback(
      (c) => c.getSignatureStatus(signature, config),
      "getSignatureStatus",
    );
  }
}

// Factory function
export function createResilientConnection(config?: ConnectionConfig): ResilientConnection {
  return new ResilientConnection(getRpcEndpoints(), config);
}

// Istanza globale pronta all'uso
export const connection = new ResilientConnection(getRpcEndpoints());
